import { useEffect, useState } from 'react';
import { useEffectiveA11yPreferences } from '@/stores/a11y-preferences-store';

export const EFFECTIVE_VIEWPORT_COMPACT_PX = 768;

function readEffectiveWidth(): number {
  if (typeof window === 'undefined') return EFFECTIVE_VIEWPORT_COMPACT_PX;
  return window.visualViewport?.width ?? window.innerWidth;
}

export interface EffectiveViewport {
  width: number;
  isCompact: boolean;
  isWide: boolean;
}

/**
 * Zoom-aware viewport: uses Visual Viewport API so browser zoom triggers compact
 * layout the same way a narrow window does (WCAG 1.4.10 reflow).
 * Honors accessibility display-layout override from the a11y menu.
 */
export function useEffectiveViewport(
  breakpoint = EFFECTIVE_VIEWPORT_COMPACT_PX,
): EffectiveViewport {
  const [width, setWidth] = useState(readEffectiveWidth);
  const { displayLayout } = useEffectiveA11yPreferences();

  useEffect(() => {
    const update = () => setWidth(readEffectiveWidth());
    update();
    const vv = window.visualViewport;
    vv?.addEventListener('resize', update);
    vv?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    return () => {
      vv?.removeEventListener('resize', update);
      vv?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  const naturalCompact = width < breakpoint;
  const isCompact =
    displayLayout === 'compact' ? true : displayLayout === 'comfortable' ? false : naturalCompact;

  return { width, isCompact, isWide: !isCompact };
}
